import { LinearProgress, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ErrorOutlineOutlined from '@mui/icons-material/ErrorOutlineOutlined';
import GroupAddOutlined from '@mui/icons-material/GroupAddOutlined';
import PublicOutlined from '@mui/icons-material/PublicOutlined';
import TaskAltOutlined from '@mui/icons-material/TaskAltOutlined';
import { DirectoryPage } from '../components/DirectoryPage';
import { lastActivityColumn, statusColumn } from '../components/columns';
import { NameCell } from '../components/NameCell';
import { ServiceChips } from '../components/ServiceChips';
import { AddAgentDialog } from '../features/agents/AddAgentDialog';
import type { Agent } from '../features/agents/sample';
import { useAgentStats } from '../features/agents/stats';
import { useAgents } from '../features/agents/useAgents';
import { shortDate } from '../lib/dates';
import { SERVICES, type Service } from '../lib/services';

/** The win rate as a bar with its percentage */
function WinRate({ rate }: { rate: number }) {
  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: 'center', minWidth: 110 }}>
      <LinearProgress variant="determinate" value={rate} color="primary" sx={{ flex: 1, height: 6, borderRadius: 3 }} />
      <Typography variant="caption" sx={{ fontWeight: 700 }}>
        {rate}%
      </Typography>
    </Stack>
  );
}

/** Every overseas agent, with how often their rates won. A click opens the agent. */
export function AgentsPage() {
  const navigate = useNavigate();
  const agents = useAgents();
  const statsOf = useAgentStats();
  const all = agents.items;
  return (
    <DirectoryPage<Agent>
      title="Overseas Agents"
      subtitle="Manage your overseas agents, compare performance, and view rate history."
      addLabel="Add Agent"
      noun="agents"
      items={all}
      tiles={{
        total: { title: 'Total Agents', note: 'All registered agents', icon: <PublicOutlined />, tone: 'info' },
        active: { title: 'Active Agents', note: 'With recent activity', icon: <TaskAltOutlined />, tone: 'success' },
        fresh: { title: 'New Agents', note: 'Added in the last 30 days', icon: <GroupAddOutlined />, tone: 'info' },
        attention: {
          title: 'Needs Attention',
          note: 'Low response or ongoing issues',
          icon: <ErrorOutlineOutlined />,
          tone: 'error',
        },
      }}
      needsAttention={(a) => statsOf(a).needsAttention}
      searchText={(a) => [a.name, a.contact, a.email, a.country].join(' ')}
      searchPlaceholder="Search agents by name, contact, email, country…"
      filters={[
        {
          label: 'Country',
          options: [...new Set(all.map((a) => a.country))].sort(),
          matches: (a, v) => a.country === v,
        },
        { label: 'Service', options: SERVICES, matches: (a, v) => a.services.includes(v as Service) },
      ]}
      rowKey={(a) => a.id}
      onOpen={(a) => void navigate(`/agents/${a.id}`)}
      columns={[
        { header: 'Agent Name', primary: true, render: (a) => <NameCell name={a.name} /> },
        { header: 'Country', render: (a) => a.country },
        { header: 'Contact Person', render: (a) => a.contact },
        { header: 'Email', render: (a) => a.email },
        { header: 'Services', render: (a) => <ServiceChips services={a.services} /> },
        { header: 'Total RFQs', nowrap: true, render: (a) => statsOf(a).total },
        { header: 'Won', nowrap: true, render: (a) => statsOf(a).won },
        { header: 'Lost', nowrap: true, render: (a) => statsOf(a).lost },
        { header: 'Win Rate', nowrap: true, render: (a) => <WinRate rate={statsOf(a).winRate} /> },
        lastActivityColumn((a) => (statsOf(a).lastActivity ? shortDate(statsOf(a).lastActivity) : '')),
        statusColumn(),
      ]}
      addDialog={(d) => <AddAgentDialog {...d} onAdd={agents.add} />}
    />
  );
}
